import { debounce } from 'lodash-es';
import { makeTDAClient, makeTDAClientProtocol, TDAClientApi } from '@barracuda/tda-api-client';
import { makeVRMClient, makeVRMClientProtocol, VRMClientApi } from '@barracuda/vrm-api-client';
import { isTokenCloseToExpired, isTokenExpired, sopClient } from './Auth';
import { sopaliticsApi } from '../analytics/sopalitics';
import jwt_decode from 'jwt-decode';
import { TestTokenPayload } from '@barracuda/shared/api/types';
import { nowMilliseconds } from '../utils/time';

const debug = process.env.REACT_APP_MODE === 'dev';

const TOKEN_KEY = 'testToken';

const VALIDATE_TOKEN_SECONDS = 10;
const ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS = 30;
const ACTIVITY_TIMEOUT_MINUTES = 10;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

let testLicenseId: number | undefined;
let testProctoringRequestId: string | undefined;
let currentTestToken: string | undefined;
let testAccessToken: string | undefined;
let refreshInProgress = false;
let lastActiveAt = nowMilliseconds();

let storageArea = window.localStorage;

export const setStorageArea = (area: Storage) => {
  storageArea = area;
};

export const markUserPresence = async () => {
  lastActiveAt = nowMilliseconds();
};

const markUserPresenceDebounced = debounce(
  markUserPresence,
  ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000,
  { leading: true, maxWait: ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000 }
);

ACTIVITY_EVENTS.forEach((eventName) => {
  document.addEventListener(eventName, markUserPresenceDebounced, {
    passive: true
  });
});

export const tdaClient: TDAClientApi = makeTDAClient(
  makeTDAClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_TEST_DELIVERY_URL as string
  })
);

export const vrmClient: VRMClientApi = makeVRMClient(
  makeVRMClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_REMOTE_MONITORING_URL as string
  })
);

const dispatchStorageEvent = (key: string, oldValue?: string, newValue?: string) => {
  if (debug) {
    console.log('TestAuth.dispatchStorageEvent', key, oldValue, newValue, storageArea);
  }

  window.dispatchEvent(
    new StorageEvent('storage', {
      storageArea,
      key,
      oldValue,
      newValue
    })
  );
};

export const getTestAccessToken = () =>
  testAccessToken ||
  sessionStorage.getItem('testAccessToken') ||
  localStorage.getItem('testAccessToken') ||
  undefined;

export const setTestAccessToken = (newAccessToken?: string) => {
  testAccessToken = newAccessToken;

  if (newAccessToken) {
    storageArea.setItem('testAccessToken', newAccessToken);
  } else {
    storageArea.removeItem('testAccessToken');
  }
};

// at first run it is not yet stored in sop client
export const getTestToken = () =>
  currentTestToken ||
  sessionStorage.getItem(TOKEN_KEY) ||
  localStorage.getItem(TOKEN_KEY) ||
  undefined;

// at first run it is not yet stored in sop client
export const getTestLicenseId = (): number | undefined => {
  if (testLicenseId) {
    return testLicenseId;
  }

  const testToken = getTestToken();
  if (testToken) {
    const testTokenPayload = findTestTokenPayload(testToken);
    testLicenseId = testTokenPayload?.licenseId;
    testProctoringRequestId = testTokenPayload?.proctoringRequestId;

    return testLicenseId;
  }

  return undefined;
};

export const getTestProctoringRequestId = (): string | undefined => {
  if (testProctoringRequestId) {
    return testProctoringRequestId;
  }

  const testToken = getTestToken();
  if (testToken) {
    const testTokenPayload = findTestTokenPayload(testToken);
    testLicenseId = testTokenPayload?.licenseId;
    testProctoringRequestId = testTokenPayload?.proctoringRequestId;

    return testProctoringRequestId;
  }

  return undefined;
};

export const removeTestToken = () => {
  setTestToken();
};

export const loadExistingTestToken = () => {
  setTestToken(getTestToken());
};

export const setTestToken = (token?: string) => {
  if (debug) {
    console.log(`TestAuth.setTestToken(${token})`);
  }

  const oldTestToken = currentTestToken;
  if (token) {
    currentTestToken = token;
    tdaClient.session.setToken(token);
    vrmClient.session.setToken(token);
    storageArea.setItem(TOKEN_KEY, token);

    // Re-derive from the new token. Must NOT use getTestLicenseId/getTestProctoringRequestId
    // here: those short-circuit on the stale module-level cache and would keep the previous
    // session's values, causing reconnect to upload frames to the old proctoringRequestId.
    const testTokenPayload = findTestTokenPayload(token);
    testLicenseId = testTokenPayload?.licenseId;
    testProctoringRequestId = testTokenPayload?.proctoringRequestId;
    sopaliticsApi.setLicenseId(testLicenseId);

    checkTestToken();
  } else {
    clearTimeout(testTokenCheckTimeout);

    currentTestToken = undefined;
    tdaClient.session.deleteToken();
    vrmClient.session.deleteToken();
    storageArea.removeItem(TOKEN_KEY);

    testLicenseId = undefined;
    testProctoringRequestId = undefined;
    sopaliticsApi.setLicenseId();
  }

  if (oldTestToken !== currentTestToken) {
    dispatchStorageEvent(TOKEN_KEY, oldTestToken, currentTestToken);
  }
};

const refreshToken = async () => {
  const licenseId = getTestLicenseId();
  if (licenseId) {
    try {
      const { token } = await sopClient.refreshLicense(licenseId + '');

      setTestToken(token);

      return;
    } catch (error) {
      console.log(`There was an error during refreshing token for license: ${licenseId}`, error);
    }
  }

  removeTestToken();
};

export const findTestTokenPayload = (token: string | undefined): TestTokenPayload | undefined => {
  if (token) {
    try {
      const decodedToken: { exp: number; payload: string } = jwt_decode(token);

      if (decodedToken.payload) {
        return JSON.parse(decodedToken.payload) as TestTokenPayload;
      }
    } catch (e) {
      console.log('Token is invalid ' + token, e);
    }
  }

  return undefined;
};

let testTokenCheckTimeout: NodeJS.Timer | undefined;

const checkTestToken = () => {
  clearTimeout(testTokenCheckTimeout);
  testTokenCheckTimeout = setTimeout(() => checkTestToken(), VALIDATE_TOKEN_SECONDS * 1000);

  const testToken = getTestToken();
  if (testToken) {
    if (isTokenExpired(testToken)) {
      removeTestToken();

      return;
    }

    if (!refreshInProgress && isTokenCloseToExpired(testToken)) {
      const now = nowMilliseconds();
      const howManyMinutesAgoWasActive = (now - lastActiveAt) / (1000 * 60);
      if (howManyMinutesAgoWasActive < ACTIVITY_TIMEOUT_MINUTES) {
        refreshInProgress = true;

        refreshToken().finally(() => (refreshInProgress = false));
      }
    }
  }
};

export const addTokenUpdatedListener = (callback?: () => void): (() => void) => {
  let listener = (e: StorageEvent) => {
    if (e.storageArea === storageArea && e.key === TOKEN_KEY) {
      if (e.oldValue !== e.newValue) {
        const token = e.newValue ? e.newValue : undefined;
        if (debug) {
          console.log('TestAuth.tokenUpdatedEvent', token, e);
        }

        setTestToken(token);

        callback?.();
      }
    }
  };

  window.addEventListener('storage', listener);
  checkTestToken();

  return () => {
    window.removeEventListener('storage', listener);
    clearTimeout(testTokenCheckTimeout);
  };
};
